import * as React from 'react';
import styled from 'styled-components';
import classNames from 'classnames';
import Workflow from '../../../models/be/Workflow';
import { populateTemplateUsingObject } from './form';
import warningSvg from '../../../images/warning.svg';
import './Paragraph.less';
import { grey26 } from '../../cssConstants';
import { isDbsStandardOrEnhancedWorkflow } from '../../../core/workflows';
import { countryIsUk } from '../../../config.local';

const aniBasicDocx = '/ANI_Basic.docx';
const aniEnhancedDocx = '/ANI_Enhanced.docx';
const aniStandardDocx = '/ANI_Standard.docx';

const Paragraph = (props: Props) => {
	const { texts, paragraphType, workflowTypeCode, className, style } = props;
	return (
		<div className={classNames('paragraph', style)}>
			{style === 'warning' && <img src={warningSvg} alt="Warning Icon" />}
			{texts?.map((text: string) => (
				<p key={text} className={classNames('paragraphText', className ?? '', style)}>
					{populateTemplateUsingObject(text, props.visibleWorkflowFields)}
				</p>
			))}
			{getParagraphByType(paragraphType, workflowTypeCode)}
		</div>
	);
};

interface Props {
	texts: string[];
	visibleWorkflowFields?: Workflow;
	paragraphType?: string;
	workflowTypeCode?: string;
	className?: string;
	style?: string;
}

Paragraph.defaultProps = {
	visibleWorkflowFields: null,
	paragraphType: null,
	workflowTypeCode: null,
	className: null,
	style: null
};

const getAniFileUrl = (workflowTypeCode: string) => {
	switch (workflowTypeCode?.toUpperCase()) {
		case 'ANIS': // ANI Standard
			return aniStandardDocx;
		case 'ANIE': // ANI Enhanced
			return aniEnhancedDocx;
		case 'ANIB': // ANI Basic
		default:
			return aniBasicDocx;
	}
};

const downloadDocument = async (fileUrl: string, fileName: string) => {
	try {
		// Fetch the file as a blob
		const response = await fetch(fileUrl);
		if (!response.ok) {
			throw new Error(`HTTP error! status: ${response.status}`);
		}

		const blob = await response.blob();

		// Create a blob URL
		const blobUrl = window.URL.createObjectURL(blob);

		// Create a temporary anchor element and trigger download
		const link = document.createElement('a');
		link.href = blobUrl;
		link.download = fileName;
		link.style.display = 'none';

		// Append to body, click, and remove
		document.body.appendChild(link);
		link.click();
		document.body.removeChild(link);

		// Clean up the blob URL
		window.URL.revokeObjectURL(blobUrl);
	} catch {
		// Fallback: try direct download
		const link = document.createElement('a');
		link.href = fileUrl;
		link.download = fileName;
		link.style.display = 'none';
		document.body.appendChild(link);
		link.click();
		document.body.removeChild(link);
	}
};

const getParagraphByType = (paragraphType: string, workflowTypeCode: string) => {
	if (paragraphType === 'dBSWelcomeText') {
		return (
			<>
				<p className="paragraphText">
					The Disclosure and Barring Service ({workflowTypeCode}) will refer the details provided on this application to
					government and law enforcement bodies in accordance with any relevant legislation. These details will be used
					for identifying possible matches to records held by them. Where such a match is established, data may be
					released to the {workflowTypeCode} for inclusion on any certificate issued. The requester and/or their
					agent(s) will receive an electronic notification once the application has been processed containing one of the
					following statements:
				</p>
				<ul className="paragraphText">
					<li>The certificate contains no information</li>
					<li>Please wait to view applicant certificate</li>
				</ul>
				<p className="paragraphText">
					The details you will provide in this application may be used to update the records held by the bodies
					specified above.
				</p>
			</>
		);
	}
	if (paragraphType === 'aniWelcomeText') {
		return (
			<>
				<p className="paragraphText accessNIContent">
					The AccessNI application must be made online directly through the{' '}
					<a
						href="https://www.nidirect.gov.uk/campaigns/accessni-criminal-record-checks"
						target="_blank"
						rel="noreferrer noopener"
					>
						nidirect website
					</a>
					, alongside the completion of this form. You will be required to set up a nidirect account to complete your
					application. The application will be checked and authorised by an approved Umbrella Body (VETTING.com) before
					being transferred to AccessNI for processing. A link to the AccessNI Privacy Notice is available{' '}
					<a
						href="https://www.justice-ni.gov.uk/sites/default/files/publications/justice/accessni-revised-privacy-notice-3-May-2018.pdf"
						target="_blank"
						rel="noreferrer noopener"
					>
						online{' '}
					</a>
					prior to commencing the e-application form.
				</p>
				<p className="paragraphText accessNIContent">
					You should note the following important information about the AccessNI application process:-
				</p>
				<ul className="paragraphText accessNIContent">
					<li>
						VETTING.com has been appointed to check and authorise your application - authorised countersignatories will
						review and process.
					</li>
					<li>You will need to upload copies of your identity and proof of address documents in the ID section.</li>
					<li>
						Your employer, and not VETTING.com, will be responsible for the decision whether or not to offer you a
						position.
					</li>
					<li>Having a criminal record does not necessarily prevent you from obtaining a position.</li>
					<li>
						{' '}
						A policy is in place that sets out your prospective employer&apos;s approach to recruiting ex-offenders.{' '}
					</li>
					<li>
						The AccessNI statutory Code of Practice is available on the nidirect website and can be viewed at:
						<a
							href="https://www.nidirect.gov.uk/sites/default/files/publications/accessni-code-of-practice.pdf"
							target="_blank"
							rel="noreferrer noopener"
						>
							https://www.nidirect.gov.uk/sites/default/files/publications/accessni-code-of-practice.pdf
						</a>
					</li>
				</ul>
				<p className="accessNIContent">
					If you have any questions in relation to this process you should, in the first instance, contact{' '}
					<a href="mailto:support@vetting.com">support@vetting.com.</a>
				</p>{' '}
			</>
		);
	}
	if (paragraphType === 'bpssWelcomeText') {
		return (
			<p className="paragraphText">
				Welcome to your BPSS clearance screening process. Thank you for your prompt attention to the completion of this
				initial form, and the onward checks that fulfil the BPSS clearance process. VETTING.com have been entrusted with
				overseeing the completion of the key background screening checks that make up your BPSS clearance. This is a
				formal process, resulting in the confirmation of the following fours categories: 1. ID verification 2. Right to
				work in the UK 3. 3 years of employment history 4. Criminal records check If you require any support through the
				process, please email support@vetting.com
			</p>
		);
	}
	if (paragraphType === 'dbsStandardAndEnhancedWelcomeText') {
		return (
			<>
				<p className="paragraphText">
					The Disclosure and Barring Service (DBS) will refer the details provided on this application to government and
					law enforcement bodies in accordance with any relevant legislation. These details will be used for identifying
					possible matches to records held by them. Where such a match is established, data may be released to the DBS
					for inclusion on any certificate issued. The requester and/or their agent(s) will receive an electronic
					notification once the application has been processed containing one of the following statements:
				</p>
				<ul className="paragraphText">
					<li>The certificate contains no information</li>
					<li>Please wait to view applicant certificate</li>
				</ul>
				<p className="paragraphText">
					The details you will provide in this application may be used to update the records held by the bodies
					specified above.
				</p>
				<p className="paragraphText">
					I provide my consent to VETTING.com and my Sponsor to fill out and manage my online Disclosure and Barring
					Service (DBS) application using information I provide to them.
				</p>
				{!isDbsStandardOrEnhancedWorkflow(workflowTypeCode) && (
					<>
						<p className="paragraphText">
							I consent to the transfer and processing of my information to First Advantage Corporation&quot;s (the
							Registered Body) systems and then to the Disclosure and Barring Service’s systems for the purpose of a
							Standard / Enhanced Disclosure check, whichever is applicable.
						</p>
						<p className="paragraphText">
							I also give consent for the electronic results to be sent to First Advantage Corporation and VETTING.com,
							and I am aware that if requested I will need to provide my Sponsor with a copy of the full Enhanced DBS
							paper certificate once I have received it at my home address.
						</p>
					</>
				)}
			</>
		);
	}
	if (paragraphType === 'dbsIdGuidelines') {
		return (
			<p className="paragraphText">
				Please provide documents in line with the{' '}
				<a
					href="https://www.gov.uk/government/publications/dbs-identity-checking-guidelines/id-checking-guidelines-for-standardenhanced-dbs-check-applications-from-1-july-2021#group-1-primary-identity-documents"
					target="_blank"
					rel="noreferrer noopener"
				>
					DBS ID Guidelines.
				</a>{' '}
				This includes one document from Group 1, and then 2 different documents from Group 1, 2a or 2b. Typically, this
				will be your current passport, your current photocard driving license and a bank statement/utility bill issued
				in the last 3 months, but please refer to the guidance for the full list of options. At least one of the
				documents must display your current address.
			</p>
		);
	}
	if (paragraphType === 'qualificationConsentText') {
		const isPqal = workflowTypeCode?.toUpperCase() === 'PQAL';
		const platform = countryIsUk ? 'UK' : 'AU';
		return QualificationConsentText(false, platform, isPqal);
	}
	if (paragraphType === 'socmWorkAndEducationMessage') {
		return (
			<p className="paragraphText">
				If any of the below does not apply to you, please write NA, however you must disclose at least 1 of the below.
			</p>
		);
	}
	if (paragraphType === 'aniStandardCheckContent') {
		return (
			<>
				<p className="paragraphText accessNIContent">
					<a href={aniStandardDocx} target="_blank" rel="noreferrer">
						try this
					</a>{' '}
					<a
						href="#"
						onClick={(e) => {
							e.preventDefault();
							downloadDocument(getAniFileUrl(workflowTypeCode || 'STANDARD'), 'ANI_Standard.docx');
						}}
						style={{ cursor: 'pointer' }}
					>
						Download instructions
					</a>{' '}
					and complete your AccessNI Online Check with{' '}
					<a
						href="https://www.nidirect.gov.uk/services/apply-online-standard-check-through-registered-body"
						target="_blank"
						rel="noreferrer noopener"
					>
						AccessNI Online Check
					</a>
				</p>
				<p className="paragraphText accessNIContent">PIN is 865141</p>
			</>
		);
	}
	if (paragraphType === 'aniEnhancedCheckContent') {
		return (
			<>
				<p className="paragraphText accessNIContent">
					<a
						href="#"
						onClick={(e) => {
							e.preventDefault();
							downloadDocument(getAniFileUrl(workflowTypeCode || 'ENHANCED'), 'ANI_Enhanced.docx');
						}}
						style={{ cursor: 'pointer' }}
					>
						Download instructions
					</a>{' '}
					and complete your AccessNI Online Check with{' '}
					<a
						href="https://www.nidirect.gov.uk/services/apply-online-enhanced-check-through-registered-body"
						target="_blank"
						rel="noreferrer noopener"
					>
						AccessNI Online Check
					</a>
				</p>
				<p className="paragraphText accessNIContent">PIN is 865141</p>
			</>
		);
	}
	return null;
};

export default Paragraph;

export const QualificationConsentText = (
	showReleaseInformation?: boolean,
	platform?: 'AU' | 'UK',
	isPqal?: boolean
) => {
	const standingType = isPqal ? 'professional' : 'academic';
	let companyName: string;
	if (platform === 'AU') {
		companyName = 'VETTING.com Pty Ltd and Qualification Check Limited';
	} else if (platform === 'UK') {
		companyName = 'VETTING.com Screening Services Ltd and Qualification Check Limited';
	} else {
		companyName = 'Qualification Check Ltd';
	}
	const privacyFramework = platform === 'AU' ? 'The Australian Privacy Principles' : 'GDPR';
	const region = platform === 'AU' ? 'Australia' : 'EEA';

	return (
		<>
			{showReleaseInformation && (
				<TextContainer>
					<BoldText className="paragraphText">Permission to Release Information</BoldText>
				</TextContainer>
			)}
			<Text className="paragraphText">
				I hereby authorise and request all parties to release information on my {standingType} standing/records to{' '}
				{companyName} for the purpose of verification in accordance with {privacyFramework}. I understand that this
				information is to be retained but will remain confidential within {companyName} and its clients and be used only
				for background screening purposes. I agree to provide any assistance or documentation required to complete and
				process any searches or applications.
			</Text>
			<Text className="paragraphText">
				I confirm that my name and associated personal data may be passed to an institution or to an agent outside the{' '}
				{region} either:
				<br />
				(a) to undertake the verification and there is a risk that such agent and/or institution may not be subject to
				the same legal framework as {companyName}; and/or <br />
				(b) for Qualification Check to share it with its subsidiaries and/or agents to assist with the processing of
				checks, and I confirm that I understand that there is a risk that the institution and/or agent may not be
				subject to the same local jurisdictional legal framework as {companyName}.
			</Text>
		</>
	);
};

const Text = styled.p`
	line-height: 20px;
	color: ${grey26};
	margin-bottom: 20px;
`;

const TextContainer = styled.div`
	display: flex;
	justify-content: center;
	align-items: center;
	margin-top: 50px;
`;

const BoldText = styled.div`
	line-height: 20px;
	color: ${grey26};
	margin-bottom: 20px;
	font-weight: bold;
	font-size: 16px;
	text-align: center;
`;
